/**
 * DonationCheer — 건전지를 넣어 준 직후의 축하 (SC-023).
 *
 * 결제가 **성공으로 끝난 뒤에만** 뜬다. 스플래시와 같은 소재의 앞 2초 —
 * 스파크가 모여 번개가 터지는 데까지다(워드마크가 맺히는 뒤쪽은 잘랐다.
 * 여기서 말할 것은 브랜드 이름이 아니라 '고맙다'이므로).
 *
 * ── 순서 ────────────────────────────────────────────────────────────
 *   0ms      영상 시작 (전체 화면, 소리 없음)
 *   ~1930ms  영상 끝 → 건전지 표식과 고맙다는 한 줄이 떠오른다
 *   +2000ms  스스로 닫힌다 (탭하면 바로 닫힌다)
 *
 * ⚠ **자동으로 닫힌다.** 축하는 잠깐 서 있다 물러나는 것이지, 사용자가 치워야
 *   하는 창이 아니다. 다만 탭하면 곧바로 닫히게 둔다 — 기다리기 싫은 사람도 있다.
 * ⚠ 영상이 안 뜨는 기기에서는(코덱·자동재생 거부) 바로 표식 단계로 넘어간다.
 *   축하가 통째로 사라지는 것보다 낫다 — AppSplash 의 폴백과 같은 판단이다.
 * ⚠ 소리는 없다. 결제 직후는 조용한 편이 안전하다.
 * ⚠ **document.body 로 portal 한다** (2026-08-27 버그 수정). 제자리에 그리면
 *   상하단 바가 축하 위로 올라온다 — z-index 를 아무리 올려도 안 된다.
 *   화면 전환 애니메이션 컨테이너(_routeMotion_)에 `will-change: transform` 이
 *   걸려 있어 **거기서 쌓임 맥락이 새로 시작하기** 때문이다. 그 안에서 9500 이든
 *   99999 든 컨테이너 자신(z-index: auto)보다 위로는 못 나간다.
 *   Sheet 가 같은 이유로 portal 을 쓴다.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/Layout";
import BatteryPictogram from "./BatteryPictogram";
import cheerVideoUrl from "../../assets/donation/peeka-cheer.mp4";
import styles from "./DonationCheer.module.css";

/**
 * 영상이 끝나고 표식이 서 있는 시간.
 * ⚠ 1400 → 2000ms (2026-08-27). 읽을 것이 셋이다 — 그림 · 고맙다 · 개수.
 *   1.4초면 그림에 눈이 닿는 사이에 사라졌다. 기다리기 싫으면 탭하면 된다.
 */
const BADGE_MS = 2000;
/** 영상이 아무 신호도 안 줄 때의 상한 — 이 시간이면 무조건 표식으로 넘어간다 */
const VIDEO_CAP_MS = 2400;

export interface DonationCheerProps {
  /** 축하가 끝났을 때 (스스로 닫힘 · 탭으로 닫힘 모두) */
  onDone: () => void;
  /** 넣은 뒤의 건전지 총 개수 — 표식에 그대로 쓴다 */
  count: number;
}

export default function DonationCheer({ onDone, count }: DonationCheerProps) {
  const { t } = useTranslation("me");
  const [phase, setPhase] = useState<"video" | "badge">("video");
  const timers = useRef<number[]>([]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const done = useRef(false);

  const finish = useCallback(() => {
    if (done.current) return;
    done.current = true;
    onDone();
  }, [onDone]);

  const toBadge = useCallback(() => {
    setPhase((prev) => (prev === "video" ? "badge" : prev));
  }, []);

  useEffect(() => {
    /* 자동재생 거부(정책·저전력 모드)는 예외가 아니라 rejected promise 로 온다 */
    const video = videoRef.current;
    const played = video?.play();
    if (played && typeof played.catch === "function") played.catch(toBadge);

    /* 영상이 끝났다는 신호가 안 와도 여기서 넘어간다 */
    timers.current.push(window.setTimeout(toBadge, VIDEO_CAP_MS));
    const list = timers.current;
    return () => {
      list.forEach(window.clearTimeout);
      list.length = 0;
    };
  }, [toBadge]);

  useEffect(() => {
    if (phase !== "badge") return;
    const id = window.setTimeout(finish, BADGE_MS);
    timers.current.push(id);
    return () => window.clearTimeout(id);
  }, [phase, finish]);

  return createPortal(
    <div
      className={styles.overlay}
      role="status"
      aria-live="polite"
      onClick={finish}
    >
      {phase === "video" ? (
        <video
          ref={videoRef}
          className={styles.video}
          src={cheerVideoUrl}
          autoPlay
          muted
          playsInline
          preload="auto"
          controls={false}
          disablePictureInPicture
          onEnded={toBadge}
          onError={toBadge}
        />
      ) : (
        <VStack gap={6} align="center" className={styles.badge}>
          {/* 표식 — 개수만큼 차오른 건전지 (2026-08-27).
              전에는 32px 아이콘에 색만 달랐다. 색은 네 단뿐이라 3개와 4개가
              같아 보였고, **몇 개째인지는 아래 글자만 알았다.** 그림이 열 장이라
              한 개씩 달라진다 — 넣을 때마다 눈에 보이는 것이 바뀐다.
              ⚠ 넣은 직후라 0 개일 수 없다. BatteryPictogram 이 0 이면 아무것도
                안 그리므로 여기서 따로 막지 않는다. */}
          <BatteryPictogram count={count} size="cheer" />

          {/* ── 위계를 뒤집었다 (2026-08-27) ────────────────────────────
              전에는 '내 건전지 4개' 가 굵게 서고 고맙다는 말이 제일 작고
              흐린 글씨로 그 아래 있었다. 그런데 **'내 건전지 4개' 는 내 정보
              목록에서 늘 보던 상태 문장**이다 — 방금 돈을 낸 사람이 여기서
              먼저 들어야 할 말이 아니다.
              이제 고맙다가 제목이고, 개수는 그 아래 한 줄이다.
              ⚠ 그래도 개수를 지우지는 않는다. 보상이 표식뿐이라 '무엇이
                늘었는지' 가 안 보이면 낸 것이 어디로 갔는지 알 수 없다. */}
          <VStack gap={1.5} align="center">
            <Heading level={2}>{t("donation.cheerThanks")}</Heading>
            {/* 개수가 아니라 **변화**를 말한다. '4개' 는 결과지만 '4개가
                모였어요' 는 방금 하나 늘었다는 뜻이 된다.
                첫 번째는 셀 것이 없으므로 따로 부른다 — 숫자 1 보다
                '첫' 이 이 순간에 맞는 말이다. */}
            <Text type="supporting" color="secondary">
              {count <= 1
                ? t("donation.cheerFirst")
                : t("donation.cheerCount", { count })}
            </Text>
          </VStack>
        </VStack>
      )}
    </div>,
    document.body,
  );
}
